import { Button, Divider, Form, InputNumber, Modal } from "antd";
import { message } from "utils/ciq-message";
import { ProjectContext } from "context/ProjectProvider";
import { Fragment, useContext, useEffect, useState } from "react";
import { MaterialCiqIcon, SubmittalCiqIcon } from "components/svg-icons";
import DisplayNameListPopover from "components/bulk-edit-material/display-name-list";
import LinkIcon from "components/svg-icons/link-icon";
import { ErrorMessages, SuccessMessages } from "constants/index";
import { validatorForFieldValue } from "utils/utils";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTAION_CREATE_MATERIAL_FROM_SUBMITTAL } from "services/graphQL/mutations";
import EllipsisTooltipInput from "./ellipsis-tooltip-input";

const parseDigitsOnly = (displayValue: string | undefined): number => {
  const digits = displayValue?.replace(/\D/g, "") ?? "";
  return digits ? Number(digits) : 0;
};

const countDuplicateMaterialNames = (
  submittals: any[],
  normalizedValue: string,
  excludeSubmittalIndex: number,
  excludeMaterialIndex: number
): number => {
  let duplicateCount = 0;
  submittals.forEach((sub, sIndex) => {
    sub.materials?.forEach((mat: any, mIndex: number) => {
      if (mat?.name?.trim()?.toLowerCase() === normalizedValue) {
        if (
          sIndex !== excludeSubmittalIndex ||
          mIndex !== excludeMaterialIndex
        ) {
          duplicateCount += 1;
        }
      }
    });
  });
  return duplicateCount;
};

type MaterialCountChangeParams = {
  form: ReturnType<typeof Form.useForm>[0];
  submittalFieldName: number;
  materialFieldsLength: number;
  selectedRowsLength: number;
  generateMaterialName: (firstName: string, index: number) => string;
  generateMaterialNameForSingleSubmittal: (
    firstName: string,
    index: number
  ) => string;
};

const handleMaterialCountChange = (
  value: number | null,
  {
    form,
    submittalFieldName,
    materialFieldsLength,
    selectedRowsLength,
    generateMaterialName,
    generateMaterialNameForSingleSubmittal
  }: MaterialCountChangeParams
) => {
  if (!value || value > 50) return;

  const firstMatName =
    form.getFieldValue([
      "submittals",
      submittalFieldName,
      "materials",
      0,
      "name"
    ]) || "Material";

  const diff = value - materialFieldsLength;
  const submittals = form.getFieldValue("submittals") || [];
  const currentMaterials = submittals[submittalFieldName]?.materials || [];
  const listHelpers = [...currentMaterials];

  if (diff > 0) {
    for (let i = 0; i < diff; i = 1 + i) {
      const newIndex = materialFieldsLength + i + 1;
      listHelpers.push({
        name:
          selectedRowsLength === 1
            ? generateMaterialNameForSingleSubmittal(firstMatName, newIndex)
            : generateMaterialName(firstMatName, newIndex)
      });
    }
  } else {
    listHelpers.splice(Number(value));
  }

  const newSubmittals = [...submittals];
  newSubmittals[submittalFieldName] = {
    ...newSubmittals[submittalFieldName],
    materials: listHelpers
  };
  form.setFieldsValue({ submittals: newSubmittals });
};

function MaterialCountInput({
  form,
  submittalFieldName,
  materialFieldsLength,
  selectedRowsLength,
  generateMaterialName,
  generateMaterialNameForSingleSubmittal
}: {
  form: ReturnType<typeof Form.useForm>[0];
  submittalFieldName: number;
  materialFieldsLength: number;
  selectedRowsLength: number;
  generateMaterialName: (firstName: string, index: number) => string;
  generateMaterialNameForSingleSubmittal: (
    firstName: string,
    index: number
  ) => string;
}) {
  const handleCountChange = (value: number | null) => {
    handleMaterialCountChange(value, {
      form,
      submittalFieldName,
      materialFieldsLength,
      selectedRowsLength,
      generateMaterialName,
      generateMaterialNameForSingleSubmittal
    });
  };

  return (
    <InputNumber
      min={1}
      max={50}
      className="w-24"
      controls
      value={materialFieldsLength}
      prefix={
        <MaterialCiqIcon size={14} className="fill-black mr-1" opacity=".45" />
      }
      parser={parseDigitsOnly}
      onChange={handleCountChange}
    />
  );
}

function MaterialNameFields({
  materialFields,
  submittalFieldName,
  duplicateMaterialValidator,
  token
}: {
  materialFields: any[];
  submittalFieldName: number;
  duplicateMaterialValidator: (
    currentSubmittalIndex: number,
    currentMaterialIndex: number
  ) => (_: any, value: string) => Promise<void>;
  token?: string;
}) {
  return (
    <div className="space-y-2">
      {materialFields.map((materialField) => (
        <Form.Item
          key={materialField.key}
          name={[materialField.name, "name"]}
          rules={[
            {
              required: true,
              validateTrigger: "onSubmit",
              message: ErrorMessages.materialTitle
            },
            {
              validator: duplicateMaterialValidator(
                submittalFieldName,
                materialField.name
              )
            },
            {
              validator: (_, value) => {
                return validatorForFieldValue(
                  "title",
                  value?.trim(),
                  "MATERIALS",
                  token,
                  ErrorMessages.MaterialTitleMsg
                );
              }
            }
          ]}
        >
          <EllipsisTooltipInput />
        </Form.Item>
      ))}
    </div>
  );
}

export default function CreateExtractMaterialFromSubmittal({
  openDrawer,
  onCancel,
  onComplete,
  selectedRows
}: {
  openDrawer: boolean;
  onCancel: () => void;
  onComplete: () => void;
  selectedRows: any[];
}) {
  const { projectDetails, tokenRetrievalState, gqlClientForProject } =
    useContext(ProjectContext);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  const [createMaterialFromSubmittalMutation] = useCIQMutation(
    MUTAION_CREATE_MATERIAL_FROM_SUBMITTAL,
    {
      client: gqlClientForProject
    }
  );

  useEffect(() => {
    if (openDrawer && selectedRows?.length > 0) {
      form.setFieldsValue({
        submittals: selectedRows.map((row) => {
          const sequenceId = row.sequence_id || "";
          const cellText =
            projectDetails?.spec_section && row.spec_section?.length > 0
              ? `${row.spec_section[0].number} - ${row?.sequence_id}`
              : `${sequenceId}`;

          const linkedMaterials =
            row.linked_items?.filter(
              (x: any) => !x.implicit && x.feature_type === 2
            ) || [];

          // For single submittal, use <Submittal Name> - 1 format
          const initialMaterialName =
            selectedRows.length === 1
              ? `${row.title} - 1`
              : `Material - ${row.title}`;

          return {
            name: `${cellText} - ${row.title}`,
            submittalId: row.id,
            materials: [{ name: initialMaterialName }],
            linkedMaterials
          };
        })
      });
    }
  }, [openDrawer, selectedRows, form, projectDetails?.spec_section]);

  const generateMaterialName = (firstName: string, index: number) => {
    // Normalize whitespace first to avoid regex backtracking issues (ReDoS)
    const normalized = firstName.trim().replace(/\s+/g, " ");

    // Case: "Material 51 - Submittal Name"
    let match = normalized.match(/^Material (\d+) - (.+)$/);
    if (match) {
      const start = Number(match[1]);
      const rest = match[2];
      return `Material ${start + index - 1} - ${rest}`;
    }

    // Case: "Material 1 Submittal Name"
    match = normalized.match(/^Material (\d+) (.+)$/);
    if (match) {
      const start = Number(match[1]);
      const rest = match[2];
      return `Material ${start + index - 1} ${rest}`;
    }

    // Case: "Material Submittal Name"
    match = normalized.match(/^Material (.+)$/);
    if (match) {
      const rest = match[1];
      return `Material ${index} ${rest}`;
    }

    // Case: "ABCD Submittal Name"
    return `Material ${index} ${firstName}`;
  };

  const generateMaterialNameForSingleSubmittal = (
    firstName: string,
    index: number
  ) => {
    const normalized = firstName.trim().replace(/\s+/g, " ");

    // Check if the name has a pattern like "Name - 1" or "Name - 2"
    const patternMatch = normalized.match(/^(.+) - (\d+)$/);

    let baseName: string;

    if (patternMatch) {
      // Extract base name (remove the number suffix like "- 1" or "- 2")
      baseName = patternMatch[1].trim();
    } else {
      // If no pattern match, the entire name is the base (user renamed it without number)
      baseName = normalized;
    }

    return `${baseName} - ${index}`;
  };

  const duplicateMaterialValidator =
    (currentSubmittalIndex: number, currentMaterialIndex: number) =>
    async (_: any, value: string) => {
      if (!value) return Promise.resolve();

      const normalizedValue = value.trim().toLowerCase();

      const submittals = form.getFieldValue("submittals") || [];

      const duplicateCount = countDuplicateMaterialNames(
        submittals,
        normalizedValue,
        currentSubmittalIndex,
        currentMaterialIndex
      );

      if (duplicateCount > 0) {
        return Promise.reject(new Error("Material name must be unique."));
      }

      return Promise.resolve();
    };

  const onFinish = async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue(true);

      const output = {
        materials: values.submittals.map((sub: any) => ({
          submittal_id: sub.submittalId,
          material_titles: sub.materials.map((x: { name: string }) => x.name)
        }))
      };

      const res = await createMaterialFromSubmittalMutation({
        variables: output
      });
      message.success(
        res.data.message || SuccessMessages.CreateMaterialsSuccess
      );
      onComplete();
    } catch (err) {
      console.error(err);
      message.error(ErrorMessages.SomethingWentWrong);
    }
    setLoading(false);
  };

  return (
    <Modal
      className=""
      title={[
        <div
          data-testid="extract-material-from-submittal-modal"
          className="px-2"
          key="create-materials-title"
        >
          <span className="text-base font-bold text-ciq-gray-950-a90">
            Create Materials
          </span>
          <span className="text-xs font-normal ml-2 mr-2">|</span>
          <span className="text-xs font-normal text-black text-opacity-60">
            {selectedRows.length === 1
              ? "1 Submittal Selected"
              : `${selectedRows.length} Submittals Selected`}
          </span>
        </div>
      ]}
      width={740}
      footer={[
        <section
          className="submittal-modal-footer flex justify-end gap-2"
          key={100}
        >
          <Button
            htmlType="reset"
            onClick={onCancel}
            disabled={loading}
            form="createMaterialFromSubmittalForm"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            disabled={loading}
            loading={loading}
            form="createMaterialFromSubmittalForm"
          >
            Create Materials
          </Button>
        </section>
      ]}
      open={openDrawer}
      onCancel={() => {
        onCancel();
      }}
      destroyOnHidden
      data-testid="index-modal-extract-material-single"
    >
      <div className="flex flex-col">
        <Form
          form={form}
          layout="vertical"
          className=""
          onFinish={onFinish}
          name="createMaterialFromSubmittalForm"
        >
          <div className="w-full flex text-xs font-bold text-black text-opacity-60">
            <div className="w-2/5 flex items-center">
              <SubmittalCiqIcon
                size={14}
                className="fill-black mr-1"
                opacity="0.45"
              />
              <div>Submittals</div>
            </div>
            <div className="w-3/5 flex">
              <MaterialCiqIcon
                size={14}
                className="fill-black mr-1"
                opacity="0.45"
              />
              <div className="grow"> Material Created</div>
            </div>
          </div>
          <div className="h-[400px] flex flex-col overflow-auto">
            <Form.List name="submittals">
              {(submittalFields) => (
                <>
                  {submittalFields.map((submittalField) => (
                    <Fragment key={submittalField.key}>
                      <Divider className="w-full my-3" />
                      <div className="w-full flex text-black text-opacity-90">
                        <div className="w-2/5 pr-1.5 flex flex-col">
                          <Form.Item
                            name={[submittalField.name, "name"]}
                            className="mb-2"
                          >
                            <div className="text-gray-700">
                              {form.getFieldValue([
                                "submittals",
                                submittalField.name,
                                "name"
                              ])}
                            </div>
                          </Form.Item>
                          <Form.Item shouldUpdate noStyle>
                            {() => {
                              const linkedMaterials =
                                form.getFieldValue([
                                  "submittals",
                                  submittalField.name,
                                  "linkedMaterials"
                                ]) || [];

                              if (!linkedMaterials.length) return null;

                              return (
                                <DisplayNameListPopover
                                  list={linkedMaterials}
                                  placement="top"
                                >
                                  <div className="inline-flex items-center text-xs text-black cursor-pointer px-1 py-px border border-solid border-ciq-gray-300 rounded bg-gray-800 bg-opacity-5 -ml-1">
                                    <LinkIcon
                                      className="mr-1 text-black text-xs"
                                      fillOpacity="0.45"
                                    />
                                    {linkedMaterials.length} Materials
                                  </div>
                                </DisplayNameListPopover>
                              );
                            }}
                          </Form.Item>

                          {/* No. of Materials – NEW POSITION */}
                          {selectedRows?.length === 1 && (
                            <div className="mt-2">
                              <div className="text-xs text-black text-opacity-60 mb-1">
                                No. of Materials
                              </div>

                              <Form.Item shouldUpdate noStyle>
                                {() => {
                                  const materialFields =
                                    form.getFieldValue([
                                      "submittals",
                                      submittalField.name,
                                      "materials"
                                    ]) || [];

                                  return (
                                    <MaterialCountInput
                                      form={form}
                                      submittalFieldName={submittalField.name}
                                      materialFieldsLength={
                                        materialFields.length
                                      }
                                      selectedRowsLength={selectedRows.length}
                                      generateMaterialName={
                                        generateMaterialName
                                      }
                                      generateMaterialNameForSingleSubmittal={
                                        generateMaterialNameForSingleSubmittal
                                      }
                                    />
                                  );
                                }}
                              </Form.Item>
                            </div>
                          )}
                        </div>

                        <div className="w-3/5">
                          <Form.List name={[submittalField.name, "materials"]}>
                            {(materialFields) => (
                              <MaterialNameFields
                                materialFields={materialFields}
                                submittalFieldName={submittalField.name}
                                duplicateMaterialValidator={
                                  duplicateMaterialValidator
                                }
                                token={tokenRetrievalState?.token}
                              />
                            )}
                          </Form.List>
                        </div>
                      </div>
                    </Fragment>
                  ))}
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
